import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Input from '../../../components/ui/Input';
import Button from '../../../components/ui/Button';
import { Checkbox } from '../../../components/ui/Checkbox';
import { supabase } from '../../../lib/supabase';
import Icon from '../../../components/AppIcon';
import { getPostAuthRedirect } from '../../../services/whopPlanService';

const RegisterForm = ({ prefillEmail = '', prefillName = '', whopToken = '' }) => {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    fullName: prefillName || '',
    email: prefillEmail || '',
    password: '',
    agreeTerms: false
  });
  const [showPassword, setShowPassword] = useState(false);
  
  // Update form data if prefill values change
  React.useEffect(() => {
    const updates = {};
    if (prefillEmail) updates.email = prefillEmail;
    if (prefillName) updates.fullName = prefillName;
    
    if (Object.keys(updates).length > 0) {
      setFormData(prev => ({ ...prev, ...updates }));
    }
  }, [prefillEmail, prefillName]);
  
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [emailSent, setEmailSent] = useState(false);
  const [whopSession, setWhopSession] = useState(null);
  
  // Fetch Whop session if token provided
  React.useEffect(() => {
    if (whopToken) {
      fetchWhopSession();
    }
  }, [whopToken]);
  
  const fetchWhopSession = async () => {
    try {
      const { data, error } = await supabase
        .from('whop_registration_sessions')
        .select('*')
        .eq('token', whopToken)
        .single();
      
      if (error) {
        console.error('Error fetching Whop session:', error);
        return;
      }
      
      if (data && !data.completed && new Date(data.expires_at) > new Date()) {
        setWhopSession(data);
        setFormData(prev => ({
          ...prev,
          email: data.email,
          fullName: data.full_name || prev.fullName
        }));
      }
    } catch (err) {
      console.error('Whop session fetch error:', err);
    }
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData?.fullName) {
      newErrors.fullName = 'Full name is required';
    } else if (formData?.fullName?.length < 2) {
      newErrors.fullName = 'Name must be at least 2 characters';
    }

    if (!formData?.email) {
      newErrors.email = 'Email is required';
    } else if (!/\S+@\S+\.\S+/.test(formData?.email)) {
      newErrors.email = 'Please enter a valid email address';
    }
    
    if (!formData?.password) {
      newErrors.password = 'Password is required';
    } else if (formData?.password?.length < 8) {
      newErrors.password = 'Password must be at least 8 characters';
    }

    if (!formData?.agreeTerms) {
      newErrors.agreeTerms = 'You must agree to the terms and conditions';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e?.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
    if (errors?.[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  const handleSubmit = async (e) => {
    e?.preventDefault();
    
    if (!validateForm()) return;

    setLoading(true);
    setErrors({});

    try {
      // Check if this is a Whop registration flow
      if (whopSession && whopToken) {
        // User already has an auth account created by webhook
        // Call edge function to complete registration
        const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
        const response = await fetch(`${supabaseUrl}/functions/v1/complete-whop-registration`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'apikey': import.meta.env.VITE_SUPABASE_ANON_KEY,
          },
          body: JSON.stringify({
            token: whopToken,
            password: formData?.password,
            fullName: formData?.fullName
          })
        });
        
        const result = await response.json();
        
        if (!response.ok || !result.success) {
          throw new Error(result.error || 'Failed to complete registration');
        }
        
        // Now sign in with the new password
        const { error: signInError } = await supabase.auth.signInWithPassword({
          email: result.email,
          password: formData?.password,
        });
        
        if (signInError) {
          throw new Error('Password set successfully but login failed. Please try logging in.');
        }
        
        // Route based on Whop plan (if any)
        try {
          // For Whop registrations, show success page first
          navigate('/whop-success');
        } catch (err) {
          console.warn('Failed to redirect to success page', err);
          navigate('/app/industry-selection');
        }
        
      } else {
        // Normal registration flow
        const { data, error } = await supabase.auth.signUp({
          email: formData?.email,
          password: formData?.password,
          options: {
            emailRedirectTo: `${window.location.origin}/app/industry-selection`,
            data: {
              full_name: formData?.fullName,
            }
          },
        });

        if (error) throw error;

        // Check if email confirmation is required
        if (data?.user && !data?.session) {
          setEmailSent(true);
        } else if (data?.session) {
          // User created and logged in, redirect to onboarding
          navigate('/app/industry-selection');
        }
      }
    } catch (error) {
      console.error('Registration error:', error);
      setErrors({
        email: error?.message || 'Registration failed. Please try again.',
        general: error?.message || 'Registration failed. Please try again.'
      });
    } finally {
      setLoading(false);
    }
  };

  if (emailSent) {
    return (
      <div className="space-y-6">
        <div className="text-center">
          <div className="w-16 h-16 bg-success/10 rounded-full flex items-center justify-center mx-auto mb-4">
            <Icon name="Mail" size={32} color="var(--color-success)" />
          </div>
          <h3 className="text-xl font-bold text-foreground mb-2">Verify Your Email</h3>
          <p className="text-muted-foreground mb-4">
            We've sent a confirmation link to <strong className="text-foreground">{formData?.email}</strong>
          </p>
          <p className="text-sm text-muted-foreground">
            Click the link in your email to verify your account and start your sales training journey!
          </p>
        </div>
        <Button
          variant="outline"
          fullWidth
          onClick={() => {
            setEmailSent(false);
            setFormData({ fullName: '', email: '', agreeTerms: false });
          }}
        >
          Use Different Email
        </Button>
      </div>
    );
  }

  return (
    <form onSubmit={handleSubmit} className="space-y-5">
      <Input
        type="text"
        name="fullName"
        label="Full Name"
        placeholder="Enter your full name"
        value={formData?.fullName}
        onChange={handleChange}
        error={errors?.fullName}
        required
        autoFocus
      />
      <Input
        type="email"
        name="email"
        label="Email Address"
        placeholder="Enter your email"
        value={formData?.email}
        onChange={handleChange}
        error={errors?.email}
        required
        disabled={!!prefillEmail}
      />
      
      <Input
        type={showPassword ? 'text' : 'password'}
        name="password"
        label="Password"
        placeholder="Create a password (min. 8 characters)"
        value={formData?.password}
        onChange={handleChange}
        error={errors?.password}
        required
        rightElement={
          <button
            type="button"
            className="text-xs text-muted-foreground hover:text-foreground"
            onClick={() => setShowPassword(!showPassword)}
          >
            {showPassword ? 'Hide' : 'Show'}
          </button>
        }
      />
      
      <Checkbox
        name="agreeTerms"
        label={
          <span className="text-sm">
            I agree to the{' '}
            <a href="/terms-of-service" target="_blank" rel="noopener noreferrer" className="text-primary hover:text-secondary font-medium">
              Terms of Service
            </a>{' '}
            and{' '}
            <a href="/privacy-policy" target="_blank" rel="noopener noreferrer" className="text-primary hover:text-secondary font-medium">
              Privacy Policy
            </a>
          </span>
        }
        checked={formData?.agreeTerms}
        onChange={handleChange}
        error={errors?.agreeTerms}
        required
      />
      
      {errors?.general && (
        <div className="text-sm text-red-600 p-2 bg-red-50 rounded">
          {errors?.general}
        </div>
      )}
      
      <Button
        type="submit"
        variant="default"
        loading={loading}
        fullWidth
        iconName="UserPlus"
        iconPosition="right"
      >
        Create Account
      </Button>

      <div className="mt-4 p-4 bg-accent/10 rounded-lg border border-accent/20">
        <div className="flex items-start gap-3">
          <Icon name="Shield" size={18} className="text-primary mt-0.5" />
          <div className="text-xs text-muted-foreground">
            <p className="font-semibold text-foreground mb-1">Secure & Private</p>
            <p>Your data is encrypted and protected. We'll send a verification link to confirm your email.</p>
          </div>
        </div>
      </div>
    </form>
  );
};

export default RegisterForm;
